/* eslint-disable no-mixed-spaces-and-tabs */
import React from 'react';
import { Snackbar, Alert, Button, IconButton, CircularProgress, Box, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector, useDispatch } from 'react-redux';
import { hideToast } from '@/features/toast/toastSlice';
import { useNavigate } from 'react-router-dom';
import { openDialog } from '@/features/dialog/dialogSlice';
import WarningIcon from '@mui/icons-material/Warning';

const SnackbarAction = ({ action, id, loading, handleClose }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	return (
		<Box display='flex' alignItems='center'>
			{action && (action.path || (action.renameFile && action.file) || action.dialogName) && (
				<Button
					variant='contained'
					color='primary'
					size='small'
					onClick={
						action.path
							? () => {
									navigate(action.path, action.params);
									dispatch(hideToast(id));
								}
							: action.renameFile && action.file
								? () => {
										dispatch(
											openDialog({
												dialogName: 'renameFile',
												data: { file: action.file },
											}),
										);
										dispatch(hideToast(id));
									}
								: action.dialogName
									? () => {
											dispatch(
												openDialog({
													dialogName: action.dialogName,
													data: action.data ?? {},
												}),
											);
											dispatch(hideToast(id));
										}
									: null
					}
				>
					{action.label}
				</Button>
			)}
			{loading && (
				<Box sx={{ pr: '1em' }}>
					<CircularProgress color='inherit' size='1em' />
				</Box>
			)}
			<IconButton
				size='small'
				aria-label='close'
				color='inherit'
				onClick={() => handleClose(id)}
			>
				<CloseIcon fontSize='small' />
			</IconButton>
		</Box>
	);
};

const Toast = () => {
	const dispatch = useDispatch();
	const toasts = useSelector((state) => state.toast.toasts);

	const handleClose = (id, reason) => {
		if (reason === 'clickaway') return;
		dispatch(hideToast(id));
	};

	return (
		<>
			{toasts.map((toast, index) => {
				const {
					id,
					instanceId,
					message,
					autohide,
					loading,
					duration,
					action,
					error,
					success,
					sx,
				} = toast;

				const key = instanceId ?? id;

				const snackbarAction = <SnackbarAction {...{ handleClose, action, loading, id }} />;

				const snackbarMessage =
					action?.renameFile && action?.file ? (
						<Stack direction='row'>
							{action?.warning && (
								<Box mr={'0.5em !important'}>
									<WarningIcon color='warning' />
								</Box>
							)}
							{message}
						</Stack>
					) : (
						message
					);

				return error ? (
					<Snackbar
						key={key}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
						open
						autoHideDuration={duration > 0 && autohide ? duration : null}
						onClose={(e, reason) => handleClose(id, reason)}
						style={{ bottom: 16 + index * 70 }}
						action={snackbarAction}
					>
						<Alert
							severity='error'
							action={snackbarAction}
							onClose={() => handleClose(id)}
						>
							{message}
						</Alert>
					</Snackbar>
				) : success ? (
					<Snackbar
						key={key}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
						open
						action={snackbarAction}
						autoHideDuration={duration > 0 && autohide ? duration : null}
						onClose={(e, reason) => handleClose(id, reason)}
						style={{ bottom: 16 + index * 70 }}
					>
						<Alert
							severity='success'
							onClose={() => handleClose(id)}
							// Snackbar ignores its own action when it has children
							{...(action && { action: snackbarAction })}
						>
							{message}
						</Alert>
					</Snackbar>
				) : (
					<Snackbar
						key={key}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
						open
						autoHideDuration={duration > 0 && autohide ? duration : null}
						onClose={(e, reason) => handleClose(id, reason)}
						message={snackbarMessage}
						action={snackbarAction}
						sx={{ ...(sx ?? {}) }}
						style={{ bottom: 16 + index * 70 }}
					/>
				);
			})}
		</>
	);
};

export default Toast;
